// Client-side chat message model + helpers, adapted from replayio/nut.new
// (`app/lib/persistence/message.ts` + `functions/mergeResponseMessages.ts`). A message is a flat
// `{ id, role, content }` record whose `content` is markdown text; streamed/updated chunks are merged
// by id. We don't stream token-by-token (the agent runs as one server round-trip per turn), but the
// merge-by-id model is kept so a message can be created empty and filled in when the reply arrives.
import type { ChatMessageData } from '../../../netlify/functions/lib/project-chat-types'
import type { TranscriptMessage } from '../../../netlify/functions/lib/project-chat-transcript'

export type { ChatMessageData }

let idCounter = 0

/** Monotonic, collision-free id for a locally-created message. */
export function nextMessageId(prefix = 'msg'): string {
  idCounter += 1
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`
}

export function createMessage(role: ChatMessageData['role'], content: string, id?: string): ChatMessageData {
  return { id: id ?? nextMessageId(role), role, content, createTime: new Date().toISOString() }
}

// nut.new's mergeResponseMessage: append a new message, or accumulate content onto an existing one
// with the same id. Returns a new array (never mutates the input) so it's safe in React state.
export function mergeMessage(messages: ChatMessageData[], incoming: ChatMessageData): ChatMessageData[] {
  const idx = messages.findIndex((m) => m.id === incoming.id)
  if (idx === -1) return [...messages, incoming]
  const next = messages.slice()
  const existing = next[idx]!
  next[idx] = { ...existing, content: existing.content + incoming.content }
  return next
}

// Set a message's content OUTRIGHT, rather than appending to it like mergeMessage.
//
// The polling transport (pollChat.ts) hands back the whole reply so far on every poll instead of a
// delta, which is what makes a poll idempotent — a repeated, duplicated or out-of-order response
// re-renders the same bubble instead of corrupting it. Feeding that through mergeMessage would append
// the entire reply to itself once a second.
export function replaceMessage(messages: ChatMessageData[], incoming: ChatMessageData): ChatMessageData[] {
  const idx = messages.findIndex((m) => m.id === incoming.id)
  if (idx === -1) return [...messages, incoming]
  const next = messages.slice()
  next[idx] = { ...next[idx]!, content: incoming.content }
  return next
}

/** Convert the UI message log into the compact shape the chat endpoint expects. `source` rides along so
 *  the transcript cap can budget QA's system-generated notices separately from the conversation; it is
 *  dropped again server-side before the transcript reaches the model. */
export function toWireMessages(messages: ChatMessageData[]): TranscriptMessage[] {
  return messages
    .filter((m) => m.content.trim())
    .map((m) => ({ role: m.role, content: m.content, source: m.source ?? null }))
}
